/* eslint-disable */
/**
 * @deprecated pharmacyService.ts — PHASE 2 DISABLED
 *
 * MODULE_PHARMACY=false in .env (backend + frontend).
 * Pharmacy inventory management is soft-disabled.
 * Prescription writing goes through consultation-prescriptions, not this.
 *
 * Rollback: set VITE_MODULE_PHARMACY=true + MODULE_PHARMACY=true + config:clear
 */
import apiClient from './apiClient';

// ==================== Types ====================

export interface MedicationCategory {
    id: number;
    name: string;
    code: string;
    description?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface MedicationUnit {
    id: number;
    name: string;
    abbreviation: string;
    description?: string;
    is_active: boolean;
}

export interface MedicationPrice {
    id: number;
    medication_id: number;
    price_type: 'private' | 'insured' | 'special';
    price: number;
    cost_price?: number;
    start_date: string;
    end_date?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface Medication {
    id: number;
    name: string;
    code: string;
    active_ingredient: string;
    category_id: number;
    category?: MedicationCategory;
    unit_id: number;
    unit?: MedicationUnit;
    description?: string;
    presentation?: string;
    concentration?: number;
    concentration_unit?: string;
    min_stock: number;
    current_stock: number;
    requires_prescription: boolean;
    is_active: boolean;
    activePrices?: MedicationPrice[];
    created_at?: string;
    updated_at?: string;
}

export interface SupplyCategory {
    id: number;
    name: string;
    code: string;
    description?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface SupplyUnit {
    id: number;
    name: string;
    abbreviation: string;
    description?: string;
    is_active: boolean;
}

export interface SupplyPrice {
    id: number;
    supply_id: number;
    price_type: 'private' | 'insured' | 'special';
    price: number;
    cost_price?: number;
    start_date: string;
    end_date?: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface Supply {
    id: number;
    name: string;
    code: string;
    category_id: number;
    category?: SupplyCategory;
    unit_id: number;
    unit?: SupplyUnit;
    description?: string;
    brand?: string;
    min_stock: number;
    current_stock: number;
    is_active: boolean;
    activePrices?: SupplyPrice[];
    created_at?: string;
    updated_at?: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    per_page: number;
    current_page: number;
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

// ==================== Medication Category Service ====================

export const medicationCategoryService = {
    async getAll(params: { per_page?: number; search?: string; is_active?: boolean } = {}): Promise<PaginatedResponse<MedicationCategory>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<PaginatedResponse<MedicationCategory>>(`/medication-categories?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<MedicationCategory> {
        const response = await apiClient.get<ApiResponse<MedicationCategory>>(`/medication-categories/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        description?: string;
        is_active?: boolean;
    }): Promise<MedicationCategory> {
        const response = await apiClient.post<ApiResponse<MedicationCategory>>('/medication-categories', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        description: string;
        is_active: boolean;
    }>): Promise<MedicationCategory> {
        const response = await apiClient.put<ApiResponse<MedicationCategory>>(`/medication-categories/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/medication-categories/${id}`);
    },
};

// ==================== Medication Unit Service ====================

export const medicationUnitService = {
    async getAll(params: { per_page?: number; is_active?: boolean } = {}): Promise<MedicationUnit[]> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<ApiResponse<MedicationUnit[]>>(`/medication-units?${queryParams.toString()}`);
        return response.data.data;
    },
};

// ==================== Medication Service ====================

export const medicationService = {
    async getAll(params: {
        page?: number;
        per_page?: number;
        search?: string;
        category_id?: number;
        is_active?: boolean;
        low_stock?: boolean;
    } = {}): Promise<PaginatedResponse<Medication>> {
        const queryParams = new URLSearchParams();
        if (params.page) queryParams.append('page', params.page.toString());
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.category_id) queryParams.append('category_id', params.category_id.toString());
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
        if (params.low_stock) queryParams.append('low_stock', 'true');

        const response = await apiClient.get<PaginatedResponse<Medication>>(`/medications?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<Medication> {
        const response = await apiClient.get<ApiResponse<Medication>>(`/medications/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        active_ingredient: string;
        category_id: number;
        unit_id: number;
        description?: string;
        presentation?: string;
        concentration?: number;
        concentration_unit?: string;
        min_stock?: number;
        current_stock?: number;
        requires_prescription?: boolean;
        is_active?: boolean;
    }): Promise<Medication> {
        const response = await apiClient.post<ApiResponse<Medication>>('/medications', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        active_ingredient: string;
        category_id: number;
        unit_id: number;
        description: string;
        presentation: string;
        concentration: number;
        concentration_unit: string;
        min_stock: number;
        current_stock: number;
        requires_prescription: boolean;
        is_active: boolean;
    }>): Promise<Medication> {
        const response = await apiClient.put<ApiResponse<Medication>>(`/medications/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/medications/${id}`);
    },

    async getPrices(medicationId: number): Promise<MedicationPrice[]> {
        const response = await apiClient.get<ApiResponse<MedicationPrice[]>>(`/medications/${medicationId}/prices`);
        return response.data.data;
    },

    async addPrice(medicationId: number, data: {
        price_type: 'private' | 'insured' | 'special';
        price: number;
        cost_price?: number;
        start_date: string;
        end_date?: string;
        is_active?: boolean;
    }): Promise<MedicationPrice> {
        const response = await apiClient.post<ApiResponse<MedicationPrice>>(`/medications/${medicationId}/prices`, data);
        return response.data.data;
    },
};

// ==================== Supply Category Service ====================

export const supplyCategoryService = {
    async getAll(params: { per_page?: number; search?: string; is_active?: boolean } = {}): Promise<PaginatedResponse<SupplyCategory>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<PaginatedResponse<SupplyCategory>>(`/supply-categories?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<SupplyCategory> {
        const response = await apiClient.get<ApiResponse<SupplyCategory>>(`/supply-categories/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        description?: string;
        is_active?: boolean;
    }): Promise<SupplyCategory> {
        const response = await apiClient.post<ApiResponse<SupplyCategory>>('/supply-categories', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        description: string;
        is_active: boolean;
    }>): Promise<SupplyCategory> {
        const response = await apiClient.put<ApiResponse<SupplyCategory>>(`/supply-categories/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/supply-categories/${id}`);
    },
};

// ==================== Supply Unit Service ====================

export const supplyUnitService = {
    async getAll(params: { per_page?: number; is_active?: boolean } = {}): Promise<SupplyUnit[]> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());

        const response = await apiClient.get<ApiResponse<SupplyUnit[]>>(`/supply-units?${queryParams.toString()}`);
        return response.data.data;
    },
};

// ==================== Supply Service ====================

export const supplyService = {
    async getAll(params: {
        per_page?: number;
        search?: string;
        category_id?: number;
        is_active?: boolean;
        low_stock?: boolean;
    } = {}): Promise<PaginatedResponse<Supply>> {
        const queryParams = new URLSearchParams();
        if (params.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params.search) queryParams.append('search', params.search);
        if (params.category_id) queryParams.append('category_id', params.category_id.toString());
        if (params.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
        if (params.low_stock) queryParams.append('low_stock', 'true');

        const response = await apiClient.get<PaginatedResponse<Supply>>(`/supplies?${queryParams.toString()}`);
        return response.data;
    },

    async getById(id: number): Promise<Supply> {
        const response = await apiClient.get<ApiResponse<Supply>>(`/supplies/${id}`);
        return response.data.data;
    },

    async create(data: {
        name: string;
        code: string;
        category_id: number;
        unit_id: number;
        description?: string;
        brand?: string;
        min_stock?: number;
        current_stock?: number;
        is_active?: boolean;
    }): Promise<Supply> {
        const response = await apiClient.post<ApiResponse<Supply>>('/supplies', data);
        return response.data.data;
    },

    async update(id: number, data: Partial<{
        name: string;
        code: string;
        category_id: number;
        unit_id: number;
        description: string;
        brand: string;
        min_stock: number;
        current_stock: number;
        is_active: boolean;
    }>): Promise<Supply> {
        const response = await apiClient.put<ApiResponse<Supply>>(`/supplies/${id}`, data);
        return response.data.data;
    },

    async delete(id: number): Promise<void> {
        await apiClient.delete(`/supplies/${id}`);
    },

    async getPrices(supplyId: number): Promise<SupplyPrice[]> {
        const response = await apiClient.get<ApiResponse<SupplyPrice[]>>(`/supplies/${supplyId}/prices`);
        return response.data.data;
    },

    async addPrice(supplyId: number, data: {
        price_type: 'private' | 'insured' | 'special';
        price: number;
        cost_price?: number;
        start_date: string;
        end_date?: string;
        is_active?: boolean;
    }): Promise<SupplyPrice> {
        const response = await apiClient.post<ApiResponse<SupplyPrice>>(`/supplies/${supplyId}/prices`, data);
        return response.data.data;
    },
};

export default {
    medicationCategoryService,
    medicationUnitService,
    medicationService,
    supplyCategoryService,
    supplyUnitService,
    supplyService,
};
